package one.healthium.onboarding.ui.screens

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import loco.GENERAL_CM_LABEL
import loco.GENERAL_ERROR_MAX_VALUE
import loco.GENERAL_ERROR_MIN_VALUE
import loco.GENERAL_FT_LABEL
import loco.Res
import one.healthium.base.utils.cmToFt
import one.healthium.onboarding.domain.configs.HeightInputScreenConfig
import one.healthium.onboarding.ui.UserDataProvider
import one.healthium.onboarding.ui.UserProfileUiModel
import one.healthium.onboarding.ui.bindPlaceholderValues
import one.healthium.onboarding.ui.models.HeightUnit
import one.healthium.ui.components.CmInput
import one.healthium.ui.components.CtaButton
import one.healthium.ui.components.FtInput
import one.healthium.ui.compose.components.AppScaffold
import one.healthium.ui.compose.components.AppSegmentedButtonRow
import one.healthium.ui.compose.components.AppText
import one.healthium.ui.compose.theme.AppTheme
import one.healthium.ui.compose.theme.gray_3
import org.jetbrains.compose.resources.stringResource

interface HeightInputEventDelegate {
    fun onNextClicked()
}

@Composable
internal fun HeightInputScreen(
    eventDelegate: HeightInputEventDelegate,
    screenConfig: HeightInputScreenConfig,
) {
    UserDataProvider { userState, contract ->
        AppScaffold {
            HeightInputScreenContent(
                screenConfig = screenConfig,
                userState = userState,
                onHeightChanged = contract::onHeightChanged,
                onHeightUnitChanged = contract::onHeightUnitChanged,
                onNextClicked = eventDelegate::onNextClicked,
            )
        }
    }
}

@Composable
internal fun HeightInputScreenContent(
    modifier: Modifier = Modifier,
    screenConfig: HeightInputScreenConfig,
    userState: UserProfileUiModel,
    onHeightChanged: (Float?) -> Unit,
    onHeightUnitChanged: (HeightUnit) -> Unit,
    onNextClicked: () -> Unit,
) {
    val height = userState.height
    val heightUnit = userState.selectedHeightUnit

    Column(
        modifier = modifier
            .fillMaxSize()
            .statusBarsPadding()
            .navigationBarsPadding()
            .padding(horizontal = 24.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        AppText(
            text = screenConfig.title?.bindPlaceholderValues(userState),
            style = AppTheme.textStyles.header,
            color = AppTheme.colors.text100,
            textAlign = TextAlign.Center,
        )
        Spacer(Modifier.height(24.dp))
        Spacer(Modifier.weight(1f))
        when (heightUnit) {
            HeightUnit.Ft -> FtInput(
                value = height,
                isValid = { height.isValid(screenConfig, HeightUnit.Ft) },
                onValueChanged = onHeightChanged,
                showKeyboardAutomaticallyIfEmpty = true,
            )

            HeightUnit.Cm -> CmInput(
                value = height,
                onValueChanged = onHeightChanged,
                showKeyboardAutomaticallyIfEmpty = true,
            )
        }
        Spacer(Modifier.height(24.dp))
        AppSegmentedButtonRow(
            options = listOf(
                stringResource(Res.string.GENERAL_FT_LABEL),
                stringResource(Res.string.GENERAL_CM_LABEL),
            ),
            selected = when (heightUnit) {
                HeightUnit.Ft -> 0
                HeightUnit.Cm -> 1
            },
            onOptionSelected = { index ->
                when (index) {
                    0 -> onHeightUnitChanged(HeightUnit.Ft)
                    1 -> onHeightUnitChanged(HeightUnit.Cm)
                }
            }
        )
        Spacer(Modifier.height(24.dp))
        val validationError = height?.getError(screenConfig, heightUnit)
        val hint = if (height != null) screenConfig.nonEmptyHint else screenConfig.defaultHint

        Box(Modifier.weight(1f)) {
            AppText(
                text = validationError ?: hint,
                style = AppTheme.textStyles.caption,
                color = gray_3,
                textAlign = TextAlign.Center,
            )
        }
        Spacer(Modifier.height(8.dp))
        CtaButton(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 14.dp),
            text = screenConfig.ctaTitle,
            enabled = validationError == null && height.isValid(screenConfig, heightUnit),
            onClick = onNextClicked,
        )
        Spacer(Modifier.height(24.dp))
        Spacer(Modifier.navigationBarsPadding().imePadding())
    }
}

@Composable
private fun Float?.getError(screenConfig: HeightInputScreenConfig, heightUnit: HeightUnit): String? {
    if (this == null || this == 0f) return null

    return when (heightUnit) {
        HeightUnit.Cm -> {
            if (this < screenConfig.minHeightCm) {
                stringResource(Res.string.GENERAL_ERROR_MIN_VALUE, screenConfig.minHeightCm)
            } else if (this > screenConfig.maxHeightCm) {
                stringResource(Res.string.GENERAL_ERROR_MAX_VALUE, screenConfig.maxHeightCm)
            } else null
        }

        HeightUnit.Ft -> {
            val valueInFt = this.cmToFt()
            if (valueInFt < screenConfig.minHeightFt) {
                stringResource(Res.string.GENERAL_ERROR_MIN_VALUE, screenConfig.minHeightFt)
            } else if (valueInFt > screenConfig.maxHeightFt) {
                stringResource(Res.string.GENERAL_ERROR_MAX_VALUE, screenConfig.maxHeightFt)
            } else null
        }
    }
}

private fun Float?.isValid(screenConfig: HeightInputScreenConfig, heightUnit: HeightUnit): Boolean {
    if (this == null) return true

    return when (heightUnit) {
        HeightUnit.Cm -> {
            this >= screenConfig.minHeightCm && this <= screenConfig.maxHeightCm
        }

        HeightUnit.Ft -> {
            this.cmToFt() >= screenConfig.minHeightFt && this.cmToFt() <= screenConfig.maxHeightFt
        }
    }
}
